import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Icon, { Icons } from '../../components/Icon/Icon';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import AppConfig from '../../config';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import { appSlice } from '../../store/slices';
import { authSlice } from '../../store/slices/auth';

import './SignUp.scss';

const { routes } = AppConfig;

const USERNAME_MIN_LENGTH = 5;
const USERNAME_MAX_LENGTH = 30;
const POSTFIX_ICON_SIZE = 20;

const SignUp = () => {
	const [agreement, setAgreement] = useState(false);
	const [notion, setNotion] = useState(false);
	const [usernameErrorLabel, setUsernameErrorLabel] = useState(null);
	const [loginAllowedTime, setLoginAllowedTime] = useState(null);
	const [remainingLockTime, setRemainingLockTime] = useState(null);
	const [loading, setLoading] = useState(false);

	const timerIdRef = useRef(null);

	const username = useSelector(state => state.auth.username);
	const captchaConfirmRequestData = useSelector(
		state => state.app.captchaConfirmRequestData
	);

	const intl = useIntl();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const updateUsername = e => {
		dispatch(authSlice.actions.setUsername(e.target.value));
		setUsernameErrorLabel(null);
	};

	const onBlurUsername = e => {
		const value = e.target.value.trim();
		dispatch(authSlice.actions.setUsername(value));

		if (!value) return;

		if (value.length < USERNAME_MIN_LENGTH) {
			setUsernameErrorLabel(
				'Username must contain at least {number} characters'
			);
			return;
		}

		if (/[^\w.-]/.test(value)) {
			setUsernameErrorLabel(
				'Only "A-Z", "a-z", "0-9", "_", "." or "-" allowed'
			);
			return;
		}
	};

	const cleanUsername = e => {
		dispatch(authSlice.actions.setUsername(''));
		setUsernameErrorLabel(null);
	};

	const updateAgreement = e => {
		setAgreement(e.target.checked);
	};

	const updateNotion = e => {
		setNotion(e.target.checked);
	};

	const startLoginAllowedTimer = () => {
		const doTick = () => {
			const timeLeft = Math.floor((loginAllowedTime - Date.now()) / 1000);
			if (timeLeft < 0) {
				clearInterval(timerIdRef.current);
			} else {
				setRemainingLockTime(timeLeft);
			}
		};

		timerIdRef.current = setInterval(() => {
			doTick();
		}, 1000);
	};

	useEffect(() => {
		if (loginAllowedTime) startLoginAllowedTimer();

		return () => {
			clearInterval(timerIdRef.current);
		};
	}, [loginAllowedTime]); // eslint-disable-line react-hooks/exhaustive-deps

	const goToCreatePin = () => {
		navigate(
			{ pathname: routes.createPin },
			{ state: { flow: 'signUp' } },
			{ replace: !!captchaConfirmRequestData }
		);
		dispatch(appSlice.actions.setCaptchaConfirmRequestData());
	};

	const onContinue = async () => {
		const resultHandler = response => {
			if (response?.Status !== 'Success') {
				if (response?.ErrorCode === 'AccountBlocked') {
					HelperService.showNotification({
						type: 'error',
						title: t('Your account has been blocked'),
					});
				} else if (response?.ErrorCode === 'LoginValidateLimitReachedError') {
					setLoginAllowedTime(
						Date.now() + response.Error.SecondsLeft * 1000 + 2000
					);
					HelperService.showNotification({
						type: 'error',
						title: t(
							'Exceeded the number of registration attempts! Login blocked for {min} min {sec} sec',
							{
								min: Math.floor(response?.Error.SecondsLeft / 60),
								sec: response?.Error.SecondsLeft % 60,
							}
						),
					});
				} else if (response?.ErrorCode === 'LoginValidateError') {
					HelperService.showNotification({
						type: 'error',
						title: t('Incorrect login'),
					});
				} else if (response) {
					throw response;
				}
			} else {
				goToCreatePin();
			}
		};

		const failHandler = error => {
			RequestService.BaseErrorFunc(error);
		};

		try {
			setLoading(true);
			const response = await RequestService.StartRegistration(
				username,
				resultHandler,
				failHandler
			);
			setLoading(false);
			resultHandler(response);
		} catch (error) {
			failHandler(error);
		}
	};

	const usernameIsValid =
		!/\s/.test(username.trim()) &&
		username.trim().length >= USERNAME_MIN_LENGTH;

	const usernamePostfix = !!usernameErrorLabel ? (
		<Icon
			className='SignUp__username-error'
			type={Icons.XMARK_CIRCLE_FILL}
			width={POSTFIX_ICON_SIZE}
			height={POSTFIX_ICON_SIZE}
			onClick={cleanUsername}
		/>
	) : usernameIsValid ? (
		<Icon
			className='SignUp__username-success'
			type={Icons.CHECKMARK_CIRCLE_FILL}
			width={POSTFIX_ICON_SIZE}
			height={POSTFIX_ICON_SIZE}
		/>
	) : null;

	const formFilled = usernameIsValid && agreement && notion;

	return (
		<div className='SignUp'>
			<PageHeader
				header={'Sign up'}
				subheader={
					'Pick a username that hasn’t been taken yet or enter your invite. You can use letters, numbers, dots, and underscores.'
				}
			/>
			<div className='SignUp__body'>
				<div>
					<Input
						className='SignUp__username'
						maxLength={USERNAME_MAX_LENGTH}
						onChange={updateUsername}
						onBlur={onBlurUsername}
						placeholder={intl.formatMessage({
							id: 'Enter username or invite name',
						})}
						value={username}
						postfix={usernamePostfix}
						error={!!usernameErrorLabel}
						subtext={t(usernameErrorLabel, { number: USERNAME_MIN_LENGTH })}
						disabled={!!remainingLockTime}
						autoFocus
					/>
					<Checkbox
						className='SignUp__agreement'
						checked={agreement}
						label={
							<span>
								{t('I agree to the')}{' '}
								<Link to={routes.stubPage}>{t('Terms of Use')}</Link>
							</span>
						}
						onChange={updateAgreement}
						disabled={!!remainingLockTime}
					/>
					<Checkbox
						className='SignUp__notion'
						checked={notion}
						label={t(
							'I understand that I can only access this wallet using the account I registered with.'
						)}
						onChange={updateNotion}
						disabled={!!remainingLockTime}
					/>
				</div>
				<Button
					className='SignUp__continue'
					onClick={onContinue}
					disabled={!formFilled || !!usernameErrorLabel || !!remainingLockTime}
					loading={loading}
				>
					{t('Continue')}
				</Button>
			</div>
		</div>
	);
};

export default SignUp;
